import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { getPaymentLogo } from '../utils/paymentLogos';
import { useLanguage } from '../context/LanguageContext';
import PageMeta from '../components/PageMeta';
import { PAYMENT_ROW_KEYS, PRICING_FALLBACK } from '../constants/pricingFallback';
import './PaiementEtComptes.css';

/** Affichage CHF / EUR pour une colonne du tableau cotisation (Under 5 = libellé gratuit). */
function formatTierPrice(chf, eur, t) {
  const chfNum = Number(chf);
  const eurNum = eur === null || eur === undefined || eur === '' ? null : Number(eur);
  if (chfNum === 0 && (eurNum === 0 || eurNum === null || Number.isNaN(eurNum))) {
    return t('paiement.tierU5TableFree');
  }
  const eurDisplay =
    eurNum === null || Number.isNaN(eurNum) ? '—' : `${eurNum} €`;
  return `${chfNum} CHF / ${eurDisplay}`;
}

const PaiementEtComptes = () => {
  const { t, language } = useLanguage();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/settings/pricing')
      .then((res) => {
        if (!cancelled) setPricing(res.data);
      })
      .catch(() => {
        if (!cancelled) setPricing(PRICING_FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const data = pricing || PRICING_FALLBACK;

  return (
    <div className="paiement-page">
      <PageMeta
        title={t('seo.paiement.title')}
        description={t('seo.paiement.description')}
        path="/paiement-et-comptes"
        locale={language}
      />
      <div className="paiement-container">
        <h1>{t('paiement.title')}</h1>

        <div className="payment-methods">
          <div className="payment-logos">
            <div className="payment-logo">
              <img src={getPaymentLogo('visamastercard.png')} alt="VISA MasterCard" />
            </div>
            <div className="payment-logo">
              <img src={getPaymentLogo('PostFinance_Logo.svg.png')} alt="PostFinance" />
            </div>
            <div className="payment-logo">
              <img src={getPaymentLogo('virement.png')} alt="Bank transfer" />
            </div>
            <div className="payment-logo">
              <img src={getPaymentLogo('png-transparent-postepay-icon-thumbnail.png')} alt="Postepay" />
            </div>
            <div className="payment-logo">
              <img src={getPaymentLogo('Revolut-Logo.png')} alt="Revolut" />
            </div>
            <div className="payment-logo">
              <img src={getPaymentLogo('lg-688f14786f85f-TWINT.webp')} alt="TWINT" />
            </div>
          </div>
        </div>

        <div className="bank-accounts-section">
          <div className="bank-account">
            <h2>{t('paiement.chfTitle')}</h2>
            <p className="account-purpose">{t('paiement.chfPurpose')}</p>
            <div className="account-details">
              <div className="account-row">
                <span className="account-label">{t('paiement.beneficiary')}</span>
                <span className="account-value">Juventus Club Oltre Frontiera</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.address')}</span>
                <span className="account-value">
                  A l&apos;att. de M. Fabio Silvestro
                  <br />
                  Chemin de Baugy 22
                  <br />
                  1815 Clarens
                </span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.iban')}</span>
                <span className="account-value account-highlight">CH52 0900 0000 1747 6158 8</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.bic')}</span>
                <span className="account-value">POFICHBEXXX</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.postal')}</span>
                <span className="account-value">17-476158-8</span>
              </div>
            </div>
          </div>

          <div className="bank-account">
            <h2>{t('paiement.eurTitle')}</h2>
            <p className="account-purpose">{t('paiement.eurPurpose')}</p>
            <div className="account-details">
              <div className="account-row">
                <span className="account-label">{t('paiement.beneficiary')}</span>
                <span className="account-value">Juventus Club Oltre Frontiera</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.address')}</span>
                <span className="account-value">
                  A l&apos;att. de M. Fabio Silvestro
                  <br />
                  Chemin de Baugy 22
                  <br />
                  1815 Clarens
                </span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.iban')}</span>
                <span className="account-value account-highlight">CH04 0900 0000 9167 8306 1</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.bic')}</span>
                <span className="account-value">POFICHBEXXX</span>
              </div>
              <div className="account-row">
                <span className="account-label">{t('paiement.postal')}</span>
                <span className="account-value">91-678306-1</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pricing-section">
          <h2>{t('paiement.pricingTitle')}</h2>

          <div className="pricing-table-container">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>{t('paiement.paymentMode')}</th>
                  <th>{t('paiement.tierAdult')}</th>
                  <th>{t('paiement.tierU14')}</th>
                  <th>{t('paiement.tierU5')}</th>
                </tr>
              </thead>
              <tbody>
                {PAYMENT_ROW_KEYS.map((key) => {
                  const row = data.cotisation[key];
                  return (
                    <tr key={key}>
                      <td>{t(`paiement.${key}`)}</td>
                      <td>{formatTierPrice(row.adultChf, row.adultEur, t)}</td>
                      <td>{formatTierPrice(row.u14Chf, row.u14Eur, t)}</td>
                      <td>{formatTierPrice(row.u5Chf, row.u5Eur, t)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pricing-note">
            <p>
              <strong>*</strong>
              {t('paiement.smsNote')}
            </p>
          </div>

          <div className="membership-types">
            <div className="membership-type">
              <h3>{t('paiement.cotisationAdultTitle')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('paiement.cotisationAdultBody') }} />
            </div>

            <div className="membership-type">
              <h3>{t('paiement.cotisationU14Title')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('paiement.cotisationU14Body') }} />
            </div>

            <div className="membership-type">
              <h3>{t('paiement.cotisationU5Title')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('paiement.cotisationU5Body') }} />
            </div>
          </div>
        </div>

        <div className="juventus-card-section">
          <h2>{t('paiement.juveCardTitle')}</h2>

          <div className="pricing-table-container">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>{t('paiement.paymentMode')}</th>
                  <th>{t('paiement.priceChf')}</th>
                  <th>{t('paiement.priceEur')}</th>
                </tr>
              </thead>
              <tbody>
                {PAYMENT_ROW_KEYS.map((key) => {
                  const row = data.juventusCard[key];
                  const eurDisplay =
                    row.eur === null || row.eur === undefined ? '—' : String(row.eur);
                  return (
                    <tr key={key}>
                      <td>{t(`paiement.${key}`)}</td>
                      <td>{row.chf}</td>
                      <td>{eurDisplay}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pricing-note">
            <p>
              <strong>*</strong>
              {t('paiement.smsNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaiementEtComptes;
